import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Typography, Chip, IconButton } from "@mui/material";
import { makeStyles } from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import {
  grayColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";
import AuthContext from "connections/auth/AuthProvider.js";
import TagInput from "./TagInput";

const useStyles = makeStyles((theme) => ({
  card: {
    border: `1px solid ${grayColor[5]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    width: 180,
    height: 120,
    minWidth: 160,
    minHeight: 120,
    maxWidth: 180,
    maxHeight: 120,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundColor: whiteColor,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      borderColor: grayColor[4],
    },
  },
  variantName: {
    fontWeight: 500,
    fontSize: "0.9rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: theme.spacing(0.5),
  },
  tagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    alignItems: "center",
    marginTop: "auto",
    overflow: "hidden",
  },
  tagChip: (props) => ({
    height: 22,
    fontSize: "0.7rem",
    backgroundColor: `${props.clientColor}15`,
    border: `1px solid ${props.clientColor}40`,
    color: props.clientColor,
    "& .MuiChip-deleteIcon": {
      fontSize: "0.8rem",
      color: props.clientColor,
      "&:hover": {
        color: "#d32f2f",
      },
    },
  }),
  addButton: {
    height: 22,
    width: 22,
    minWidth: 22,
    border: `1px dashed ${grayColor[6]}`,
    borderRadius: "50%",
    padding: 0,
    "&:hover": {
      backgroundColor: grayColor[12],
    },
  },
  addIcon: {
    fontSize: "0.9rem",
    color: grayColor[4],
  },
  noTags: {
    color: grayColor[4],
    fontSize: "0.7rem",
    fontStyle: "italic",
  },
}));

const VariantTagCard = ({ variant, availableTags, onAddTag, onRemoveTag }) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });
  const [showInput, setShowInput] = useState(false);

  const currentTags = variant.tags || [];

  const handleAddTag = (tagName) => {
    // Handle "Create: xyz" format from autocomplete
    const createPrefix = t("create_tag", { name: "" }).replace('""', "");
    if (tagName.startsWith(createPrefix)) {
      tagName = tagName.replace(createPrefix, "").replace(/"/g, "").trim();
    }

    if (tagName && !currentTags.includes(tagName)) {
      onAddTag(variant.variant_id, tagName);
    }
    setShowInput(false);
  };

  const handleRemoveTag = (tagToRemove) => {
    onRemoveTag(variant.variant_id, tagToRemove);
  };

  return (
    <Box className={classes.card}>
      <Typography className={classes.variantName} title={variant.variant_name}>
        {variant.variant_name}
      </Typography>

      <Box className={classes.tagsContainer}>
        {currentTags.length === 0 && !showInput && (
          <Typography className={classes.noTags}>{t("no_tags")}</Typography>
        )}

        {currentTags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            className={classes.tagChip}
            onDelete={() => handleRemoveTag(tag)}
          />
        ))}

        {showInput ? (
          <TagInput
            availableTags={availableTags}
            currentTags={currentTags}
            onSelect={handleAddTag}
            onClose={() => setShowInput(false)}
          />
        ) : (
          <IconButton
            className={classes.addButton}
            size="small"
            onClick={() => setShowInput(true)}
            title={t("add_tag")}
          >
            <AddIcon className={classes.addIcon} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

VariantTagCard.propTypes = {
  variant: PropTypes.shape({
    variant_id: PropTypes.number.isRequired,
    variant_name: PropTypes.string.isRequired,
    tags: PropTypes.array,
  }).isRequired,
  availableTags: PropTypes.array.isRequired,
  onAddTag: PropTypes.func.isRequired,
  onRemoveTag: PropTypes.func.isRequired,
};

export default VariantTagCard;
